import type { AxiosInstance } from "axios";
import type { ReactNode } from "react";
import { useCallback, useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, U2000FormStateEnumState, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import {
  doGetChildLotChainAuditsData,
  doGetChildLotChainData,
  doFetchPropertyIndex,
  doLockChildLotChainData,
  doUnlockChildLotChainData,
  doUpdateChildLotChainData,
  getIndexDocumentId,
  luHandleAppiError,
  U3141EmptyChildLotChain,
  type U3141ChildLotChain,
} from "../U3141Service";
import { useU3141Context } from "../U3141Context";
import { useU3141UseGetChildLotChainHandlers } from "./form/u3141ChildLotChain/U3141UseGetChildLotChainHandlers";
import { NS, U3141ROLES } from "../U3141";
import { U3141ChildLotChainFormToolBar } from "./form/u3141ChildLotChain/U3141ChildLotChainFormToolBar";
import U3141ChildLotChainBody from "./form/u3141ChildLotChain/U3141ChildLotChainBody";
import { U3141ChildLotChainDocumentsTab } from "./form/u3141ChildLotChain/U3141ChildLotChainDocumentsTab";
import { useU3141UseLockChildLotChainHandlers } from "./form/u3141ChildLotChain/U3141UseLockChildLotChainHandlers";
import { useU3141UseUnLockChildLotChainHandlers } from "./form/u3141ChildLotChain/U3141UseUnLockChildLotChainHandlers";
import { useU3141UseFetchPropertyIndexHandlers } from "./form/u3141ChildLotChain/U3141UseFetchPropertyIndexHandlers";
import { useU3141FetchPropertyIndexNotification } from "./form/u3141ChildLotChain/U3141UseFetchPropertyIndexNotification";
import { useU3141OpenDocumentPdfFileHandlers } from "./form/u3141ChildLotChain/U3141UseOpenDocumentPdfFileHandlers";
import { validateConcordanceEntries } from "./form/u3141ChildLotChain/U3141ConcordanceUtils";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000YesNoDialog from "../../u2000Core/components/dialog/U2000YesNoDialog";

// Interface à placer en dehors du composant pour éviter l'erreur de modificateur
export interface U3141ChildLotChainFormHandlers extends U2000FormStateHandlersReturn<U3141ChildLotChain> {
  modifyChildLotChain: () => void;
  lockChildLotChain: () => void;
  unlockChildLotChain: () => void;
  fetchPropertyIndex: () => void;
  viewDocument: () => void;
}

// Composant principal pour le formulaire de chaîne de lots enfants
const U3141ChildLotChainForm: React.FC = () => {
  // Parameters
  const { id, childLotChainId } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  // manage form initialization state
  const [hasInitialized, setHasInitialized] = useState(false);

  // Form state and reducer for u2101LogicalUnitBL
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- useReducer action type must satisfy React's AnyActionArg; U2000FormAction is incompatible
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  // manage active tab state
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Popup confirmation avant récupération de l'index des immeubles (frais, notification)
  const [isFetchPropertyIndexConfirmOpen, setFetchPropertyIndexConfirmOpen] = useState(false);
  const [isFetchPropertyIndexCooldown, setFetchPropertyIndexCooldown] = useState(false);

  // navigate et location
  const location = useLocation();
  const navigate = useNavigate();

  // Translation
  const { t } = useTranslation();
  const toast = useToast();

  // API Axios Client
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  // LU Handler,  Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridMasterChildLotChainStateHandlers } = useU3141Context();

  // Form Handlers and default methods
  const validateForm = (childLotChain: U3141ChildLotChain) => {
    const result: Record<string, string> = {};
    if (!childLotChain?.lotDetail.district?.id) result["lotDetail.district.id"] = t("errDistrictRequired", { ns: NS });
    if (!childLotChain?.lotDetail.cadastre?.id) result["lotDetail.cadastre.id"] = t("errCadastreRequired", { ns: NS });
    if (!childLotChain?.lotDetail.dateEstablishment) result["lotDetail.dateEstablishment"] = t("errDateEstablishmentRequired", { ns: NS });
    const concordance = childLotChain?.lotDetail?.concordance ?? [];
    const concordanceValidation = validateConcordanceEntries(concordance);
    if (!concordanceValidation.valid) {
      result["lotDetail.concordance"] = t("errConcordanceInvalid", { ns: NS });
    }
    return result;
  };

  // Business Logic Handlers for U3141ChildLotChain
  const u3141ChildLotChainBL: U2000FormSBusinessHandlers<U3141ChildLotChain> = {
    newRecord: () => {
      return U3141EmptyChildLotChain;
    },

    // validation du formulaire
    validateForm: validateForm,

    // sauvegarde du formulaire
    saveForme: async (childLotChain: U3141ChildLotChain, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(childLotChain);
      if (Object.keys(errors).length === 0) {
        handleSave.saveData(childLotChain, editMode);
        u3141ChildLotChainFormStateExtendedHandlers.setCustomAttributes({});
      } else {
        u3141ChildLotChainFormStateExtendedHandlers.setErrors(errors);
      }
    },

    // annulation du formulaire
    cancelForm: () => {
      const childLotChain = u3141ChildLotChainFormStateExtendedHandlers.getOldData() as U3141ChildLotChain | null;
      if (childLotChain) {
        type ChildLotChainWithNestedGrid = U3141ChildLotChain & {
          childLotChains?: Array<U3141ChildLotChain & { idChecked?: boolean }>;
        };
        const row = childLotChain as ChildLotChainWithNestedGrid;
        let restored: U3141ChildLotChain = { ...childLotChain };
        if (Array.isArray(row.childLotChains)) {
          restored = {
            ...childLotChain,
            childLotChains: row.childLotChains.map((c) => ({ ...c, idChecked: false })),
          } as U3141ChildLotChain;
        }
        u3141ChildLotChainFormStateExtendedHandlers.setData(restored);
      }
      u3141ChildLotChainFormStateExtendedHandlers.setCustomAttributes({});

      if (luStateHandlers.getActionOriginFrom()?.startsWith("/U3141")) {
        navigate(`/U3141/${id}`, { replace: true });
      }
    },

    // gestion du changement d'enregistrement
    onChangeRecordForm: (childLotChain) => {
      if (childLotChain.id) {
        handleGet.getChildLotChain(id!, childLotChain.id);
      }
    },

    // gestion de la sortie du formulaire
    onLeaveForm: () => {
      navigate(`/U3141/${id}`, { replace: true });
    },
  };

  // Handlers pour le formulaire d'un rôle
  const u3141ChildLotChainFormStateHandlers = useU2000FormStateHandlers<U3141ChildLotChain>({
    formState,
    formStateDispatch,
    businessHandlers: u3141ChildLotChainBL,
  });

  const handleApiErrorToast = useCallback(
    ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [t, toast]
  );

  /** BL-0472 — consulter index via pdf-file (NAS / cache S3). */
  const { openDocumentPdfFile } = useU3141OpenDocumentPdfFileHandlers({
    axios: u2000AxiosAuthenticated,
    onError: handleApiErrorToast,
  });

  // extended methods
  const u3141ChildLotChainFormStateExtendedHandlers: U3141ChildLotChainFormHandlers = {
    ...u3141ChildLotChainFormStateHandlers,

    modifyChildLotChain: () => {
      u3141ChildLotChainFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },

    lockChildLotChain: () => {
      handleLockChildLotChain.lockChildLotChain(id!, childLotChainId!);
    },

    unlockChildLotChain: () => {
      handleUnLockChildLotChain.unLockChildLotChain(id!, childLotChainId!);
    },

    fetchPropertyIndex: () => {
      setFetchPropertyIndexConfirmOpen(true);
    },

    viewDocument: () => {
      const data = u3141ChildLotChainFormStateExtendedHandlers.getData() as U3141ChildLotChain | undefined;
      const documentId = getIndexDocumentId(data?.lotDetail);

      if (documentId) {
        void openDocumentPdfFile(documentId);
      } else {
        toast.error(t("errorNoDocumentUrl", { ns: NS }), t("errorTitle", { ns: NS }));
      }
    },
  };

  // Call Get Handlers for Logical Unit
  const handleGet = useU3141UseGetChildLotChainHandlers<U3141ChildLotChain & { id: string; childLotChainId: string }>({
    doGetFn: (id: string, childLotChainId: string) => {
      return doGetChildLotChainData(u2000AxiosAuthenticated, id, childLotChainId);
    },
    onSuccess: (childLotChain: U3141ChildLotChain & { id: string; childLotChainId: string }) => {
      gridMasterChildLotChainStateHandlers.setDataById(childLotChain.id, childLotChain);
      u3141ChildLotChainFormStateExtendedHandlers.setData(childLotChain);

      // Remplace l'id dans l'URL par le nouvel id
      const newPath = location.pathname.replace(/[\w-]+$/, String(childLotChain.id));
      navigate(newPath, { replace: true });

      setLogicalUnitBreadcrumb([
        { href: "/U3141", label: t("title", { ns: NS }) },
        { href: `/U3141/${childLotChain.masterLotChain.id}`, label: childLotChain.masterLotChain.projectDescription + " (" + childLotChain.masterLotChain.projectNumber + ")" },
        { label: childLotChain.lotDetail.lot },
      ]);

      // Détermine le mode du formulaire selon l'action précédente
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3141ChildLotChainFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Save Handlers for Logical Unit
  const handleSave = useU2000SaveHandlers<U3141ChildLotChain>({
    doSaveFn: (childLotChain: U3141ChildLotChain, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateChildLotChainData(u2000AxiosAuthenticated, id!, childLotChain.id, childLotChain);
      }
      // Ensure a Promise is always returned to satisfy the handler's expected signature
      return Promise.resolve(undefined);
    },
    onSuccess: (childLotChain: U3141ChildLotChain & { id: string }, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridMasterChildLotChainStateHandlers.setData([...gridMasterChildLotChainStateHandlers.getData(), childLotChain]);
        gridMasterChildLotChainStateHandlers.setTotalRows(gridMasterChildLotChainStateHandlers.getTotalRows() + 1);
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridMasterChildLotChainStateHandlers.setDataById(childLotChain.id, childLotChain);
      }
      u3141ChildLotChainFormStateExtendedHandlers.afterSaveData(childLotChain);
      toast.success(t("msgChildLotChainSaved", { ns: NS }));

      // Retourne au formulaire lot maître après l'ajout ou la modification selon l'origine
      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom()?.startsWith("/U3141")) {
          navigate(`/U3141/${id}`, { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);

      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3141ChildLotChainFormStateExtendedHandlers.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3141ChildLotChainFormStateExtendedHandlers.setErrors(errorResult.fieldErrors);
      }
    },
  });

  // Handlers to Lock Child Lot Chain
  const handleLockChildLotChain = useU3141UseLockChildLotChainHandlers<U3141ChildLotChain & { id: string; childLotChainId: string }>({
    doLockFn: (id: string, childLotChainId: string) => {
      return doLockChildLotChainData(u2000AxiosAuthenticated, id, childLotChainId);
    },
    onSuccess: (childLotChain: U3141ChildLotChain & { id: string; childLotChainId: string }) => {
      gridMasterChildLotChainStateHandlers.setDataById(childLotChain.id, childLotChain);
      u3141ChildLotChainFormStateExtendedHandlers.setData(childLotChain);
      toast.success(t("msgChildLotChainLocked", { ns: NS }));
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Handlers to UnLock Child Lot Chain
  const handleUnLockChildLotChain = useU3141UseUnLockChildLotChainHandlers<U3141ChildLotChain & { id: string; childLotChainId: string }>({
    doUnLockFn: (id: string, childLotChainId: string) => {
      return doUnlockChildLotChainData(u2000AxiosAuthenticated, id, childLotChainId);
    },
    onSuccess: (childLotChain: U3141ChildLotChain & { id: string; childLotChainId: string }) => {
      gridMasterChildLotChainStateHandlers.setDataById(childLotChain.id, childLotChain);
      u3141ChildLotChainFormStateExtendedHandlers.setData(childLotChain);
      toast.success(t("msgChildLotChainUnlocked", { ns: NS }));
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleFetchPropertyIndex = useU3141UseFetchPropertyIndexHandlers({
    doFetchPropertyIndexFn: (masterId: string, childId: string) => {
      return doFetchPropertyIndex(u2000AxiosAuthenticated, masterId, childId);
    },
    onSuccess: (jobId) => {
      toast.success(t("msgFetchPropertyIndexPending", { jobId: jobId || "—", ns: NS }));
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Notification WebSocket : refetch du lot enfant quand le job import registre est terminé (uniquement en mode lecture)
  useU3141FetchPropertyIndexNotification(id ?? undefined, childLotChainId ?? undefined, (masterId, childId) => {
    if (u3141ChildLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.READ) {
      handleGet.getChildLotChain(masterId, childId);
    }
  });

  // Wrapper adapté à U2000AuditPanelComponent: (axiosInstance, id) => Promise<...>
  const doGetChildLotChainAuditsWrapper = useCallback(
    (axiosInstance: AxiosInstance, childLotChainId: string | number) => {
      return doGetChildLotChainAuditsData(axiosInstance, id!, childLotChainId!);
    },
    [id],
  );

  const COOLDOWN_MS = 45000;

  const handleFetchPropertyIndexConfirmClose = (result: "yes" | "no") => {
    if (result === "yes" && id && childLotChainId) {
      setFetchPropertyIndexCooldown(true);
      handleFetchPropertyIndex.fetchPropertyIndex(id, childLotChainId);
      setTimeout(() => setFetchPropertyIndexCooldown(false), COOLDOWN_MS);
    }
    setFetchPropertyIndexConfirmOpen(false);
  };

  // Initialisation de lors du chargement de l'élément
  useEffect(() => {
    if (id && childLotChainId && !hasInitialized) {
      if (childLotChainId === "new") {
        u3141ChildLotChainFormStateExtendedHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
        u3141ChildLotChainFormStateExtendedHandlers.setData({ ...U3141EmptyChildLotChain });
      } else if (id && childLotChainId) {
        handleGet.getChildLotChain(id, childLotChainId);
      } else {
        toast.error(t("errInvalidChildId", { ns: NS }));
      }
    }
    setHasInitialized(true);
  }, [id, childLotChainId, hasInitialized]);

  const childData = u3141ChildLotChainFormStateExtendedHandlers.getData() as U3141ChildLotChain | undefined;

  interface U3141ChildLotChainFormTab {
    id: string;
    label: string;
    content: ReactNode;
  }

  const formTabsDefs: (U3141ChildLotChainFormTab | false)[] = [
    {
      id: "tabDocuments",
      label: t("tabDocuments", { ns: NS }),
      content: (
        <U3141ChildLotChainDocumentsTab
          masterLotChainId={id ?? ""}
          childLotChainId={childLotChainId ?? ""}
          childLotChain={childData ?? null}
          onDocumentsChange={() => {
            if (id && childLotChainId) handleGet.getChildLotChain(id, childLotChainId);
          }}
          disableDocumentActions={u3141ChildLotChainFormStateExtendedHandlers.getFormState() === U2000FormStateEnumState.EDIT}
        />
      ),
    },
    hasRole(U3141ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: <U2000AuditPanelComponent id={childData?.id ?? ""} doGetAuditsData={doGetChildLotChainAuditsWrapper} toastName="U3141ChildLotChainAudit" />,
    },
  ];
  const formTabsComponent: U3141ChildLotChainFormTab[] = formTabsDefs.filter((tab): tab is U3141ChildLotChainFormTab => tab !== false);

  const formHandlersU2000 = u3141ChildLotChainFormStateExtendedHandlers as unknown as U2000FormStateHandlers<U3141ChildLotChain>;

  // Render
  return (
    <>
      <U2000YesNoDialog isOpen={isFetchPropertyIndexConfirmOpen} onClose={handleFetchPropertyIndexConfirmClose} title={t("dlgFetchPropertyIndexTitle", { ns: NS })}>
        <p>{t("dlgFetchPropertyIndexMessage", { ns: NS })}</p>
      </U2000YesNoDialog>
      <U2000DefaultForm
        actionsBtns={<U3141ChildLotChainFormToolBar formStateHandlers={u3141ChildLotChainFormStateExtendedHandlers} fetchPropertyIndexCooldown={isFetchPropertyIndexCooldown} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridMasterChildLotChainStateHandlers}
        formStateHandlers={formHandlersU2000}
      >
        <U3141ChildLotChainBody formStateHandlers={formHandlersU2000} />

        <U2000Tab
          className="mt-4"
          tabs={formTabsComponent}
          defaultTab="tabDocuments"
          activeTab={activeTab ?? "tabDocuments"}
          setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
        />
      </U2000DefaultForm>
    </>
  );
};

export default U3141ChildLotChainForm;
